import { computed, ElementRef, inject, Injectable, Renderer2, RendererFactory2, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { map, Observable, startWith } from 'rxjs';
import { LanguageCode, LanguageDirection, LanguageFontFamily } from './translation.enum';
import { ILanguage } from './translation.interface';
import { ELanguageId } from '@pc/common';

@Injectable()
export class TranslationService {
  public translate: TranslateService = inject(TranslateService);
  public direction = computed(() => this.directionSetter());
  private rendererFactory = inject(RendererFactory2);
  private renderer: Renderer2;
  private directionSetter = signal<LanguageDirection | undefined>(undefined);

  constructor() {
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  private get languages(): ILanguage[] {
    return [
      {
        code: LanguageCode.EN_US,
        fontFamily: LanguageFontFamily.EN,
        direction: LanguageDirection.LTR
      },
      {
        code: LanguageCode.HE_IL,
        fontFamily: LanguageFontFamily.HE,
        direction: LanguageDirection.RTL
      },
      {
        code: LanguageCode.EN_AU,
        fontFamily: LanguageFontFamily.EN,
        direction: LanguageDirection.LTR
      }
    ];
  }

  public init(lang: string, languages: string[]) {
    this.translate.addLangs(languages);
    return this.translate.use(lang);
  }

  public setDefault(lang: string) {
    this.translate.setDefaultLang(lang);
  }

  public use(lang: string): Observable<unknown> {
    return this.translate.use(lang);
  }

  public getDefaultLang(): string {
    return this.translate.getDefaultLang();
  }

  public instant(key: string, interpolationParams?: object) {
    return this.translate.instant(key, interpolationParams);
  }

  public changeLanguage(elementRef: ElementRef, lang: string): Observable<unknown> {
    const dir = lang == LanguageCode.HE_IL ? LanguageDirection.RTL : LanguageDirection.LTR;
    const font: string = this.languages.find((l) => l.code === lang)?.fontFamily || LanguageCode.HE_IL;
    this.setDocumentDirection(dir, elementRef);
    this.setDocumentFont(font, elementRef);
    this.setDocumentLanguage(lang, elementRef);
    return this.translate.use(lang);
  }

  public getCurrentLang() {
    return this.translate.currentLang;
  }

  public getCurrentLangId(): number {
    switch (this.translate.currentLang) {
      case LanguageCode.HE_IL:
        return ELanguageId.Hebrew;
      case LanguageCode.DE_DE:
        return ELanguageId.German;
      case LanguageCode.FR_FR:
        return ELanguageId.French;
      default:
        return ELanguageId.English;
    }
  }

  public getCurrentLangObservable(): Observable<string> {
    return this.translate.onLangChange.pipe(
      map((languageEvent) => languageEvent.lang),
      startWith(this.getCurrentLang())
    );
  }

  public getCurrentLangIdObservable(): Observable<number> {
    return this.translate.onLangChange.pipe(
      map(() => this.getCurrentLangId()),
      startWith(this.getCurrentLangId())
    );
  }

  private setDocumentDirection(direction: LanguageDirection, elementRef: ElementRef): void {
    this.directionSetter.set(direction);
    this.renderer.setAttribute(elementRef.nativeElement.ownerDocument.documentElement, 'dir', direction);
  }

  private setDocumentLanguage(language: string, elementRef: ElementRef): void {
    const langCode = language.toLowerCase().slice(0, 2);
    this.renderer.setAttribute(elementRef.nativeElement.ownerDocument.documentElement, 'lang', langCode);
  }

  private setDocumentFont(fontFamily: string, elementRef: ElementRef): void {
    this.renderer.setStyle(elementRef.nativeElement.ownerDocument.documentElement, 'font-family', fontFamily);
  }
}
